import { useCallback, useEffect, useState } from 'react';
import BackendService from '../../../services/admin/BackendService';
import {
  InvestorStatementEmailTemplateData,
  InvestorStatementItem,
} from '../../../models/admin/InvestorStatement';
import { sanitizeEmailHTML } from '../../../services/sanitizeHTML';
import { useEditHintSpeechBubble } from '../useEditHintSpeechBubble';

/**
 * Drives StatementEmailComposePanel — same shape as useGenerateInvitationEmail
 * (live template preview + click-to-edit custom message via the shared
 * EmailPreviewIframe rich-text infrastructure, see useEditHintSpeechBubble), but scoped to
 * one InvestorStatement item and staying dialog-based (no page navigation) to
 * fit the existing confirm -> send-prompt -> compose sequencing on the
 * investor page.
 */
export function useStatementEmailCompose(item: InvestorStatementItem, onSent: () => void) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [templateData, setTemplateData] = useState<InvestorStatementEmailTemplateData | null>(null);
  const [customHtml, setCustomHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const editHint = useEditHintSpeechBubble();

  const loadTemplate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, currentUser] = await Promise.all([
        new BackendService().getInvestorStatementEmailTemplate(item.externalId),
        new BackendService().getCurrentUser(),
      ]);
      setTemplateData(data);
      setSubject(data.subject);
      setFromEmail(currentUser.email || '');
      setCustomHtml('');
    } catch (err: any) {
      setError(err?.error || 'Failed to load email template');
    } finally {
      setLoading(false);
    }
  }, [item.externalId]);
  const { resetEditHintState, emailPreviewRef, ...editHintPublic } = editHint;
  useEffect(() => {
    void loadTemplate();
    return () => resetEditHintState();
  }, [loadTemplate, resetEditHintState]);

  const handleContentChange = useCallback((html: string) => {
    setCustomHtml(sanitizeEmailHTML(html, { allowAnchors: true }));
  }, []);
  const handleSend = useCallback(async () => {
    setSending(true);
    setError(null);
    try {
      const currentHtml = emailPreviewRef.current?.getCurrentHtml();
      const sanitizedHtml = sanitizeEmailHTML(currentHtml || customHtml || '', {
        allowAnchors: true,
      });
      await new BackendService().sendInvestorStatementEmail(
        item.externalId,
        subject,
        sanitizedHtml,
      );
      onSent();
    } catch (err: any) {
      setError(err instanceof Error ? err.message : err?.error || 'Failed to send email');
    } finally {
      setSending(false);
    }
  }, [customHtml, emailPreviewRef, item.externalId, onSent, subject]);
  return {
    loading,
    sending,
    templateData,
    customHtml,
    subject,
    setSubject,
    fromEmail,
    setFromEmail,
    error,
    emailPreviewRef,
    ...editHintPublic,
    handleContentChange,
    handleSend,
  };
}
